import React, { Component } from "react";
import moment from "moment";
import json2csv from "json2csv";
import {
  WindowScroller,
  List,
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
} from "react-virtualized";
import PaymentPanel from "./../components/bulk/paymentPanel.jsx";
import NoDetailsToShow from "./../components/misc/noDetailsToShow.jsx";
import Loading from "./../components/popups/loading.jsx";
import { currencyFormatter } from "./../utils/display";
import { connect } from "react-redux";
import { defaultAction } from "../actions/defaultAction";
import * as ActionTypes from "../constants/actionTypes";
import { getSellPayment, getSellPayments } from "../thunks/customerInfo";
import {
  sortAlphaAsc,
  sortAlphaDesc,
  sortNumsAsc,
  sortNumsDesc,
} from "../utils/sort";
import { HeadLine } from "../modules/HeadLine";
import styled from "@emotion/styled";

const DownloadLink = styled.a`
  background: #ffffff;
  border-radius: 80px;
  font-family: Montserrat, serif;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  text-align: center;
  color: #c7a878;
  border: 1px solid rgba(199, 168, 120, 0.32);
  height: 40px;
  width: 200px;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    color: #c7a878;
    text-decoration: none;
    transform: translateY(-0.5px);
    border-color: #b37b28;
  }
`;

class Payments extends Component {
  constructor() {
    super();
    this._cache = new CellMeasurerCache({
      defaultHeight: 40,
      fixedWidth: true,
    });
    this._resizeAllFlag = false;
  }
  componentDidMount = () => {
    this.props.getSellPayments();
    this.props.setFieldToSort("payment id");
  };

  componentWillReceiveProps = (nextProps) => {
    const { paymentsSell, setPaymentTotal } = this.props;

    if (
      paymentsSell.get("payments") !== nextProps.paymentsSell.get("payments")
    ) {
      const id = nextProps.paymentsSell.get("payments").reverse()[0].id;
      const paymentsTotalPast90Days = nextProps.paymentsSell
        .get("payments")
        .filter((payment) => {
          const date = moment.utc(payment.date).valueOf();
          const limit = +moment().subtract(90, "days").format("x");
          return date > limit;
        })
        .reduce((prev, { amount }) => prev + Number(amount), 0);

      this.chooseActivePanel(id);
      setPaymentTotal(paymentsTotalPast90Days);
    }
  };

  componentWillUnmount = () => {
    this.props.resetActivePanel();
    this.props.clearSearchText();
    this.props.setFieldToSort("");
    this.props.setSortOrder("desc");
  };

  _resizeAll = () => {
    this._cache.clearAll();
    if (this._list) {
      this._list.recomputeRowHeights();
    }
  };

  chooseActivePanel = (id) => {
    const { userActions, resetActivePanel, setActivePanel, getSellPayment } =
      this.props;
    const panelAlreadyActive =
      userActions.get("activePanel") === id ? true : false;

    if (panelAlreadyActive) {
      return resetActivePanel();
    }

    getSellPayment(id);
    setActivePanel(id);
  };

  filterBySearch = (v) => {
    const { userActions, location } = this.props;
    const currentPage = location.pathname;
    const filterRowsById = (v) =>
      `#${v}`
        .toLowerCase()
        .indexOf(userActions.get("searchText").toLowerCase()) > -1;
    const filterOrderRows = (id, status) =>
      `#${id} ${status}`
        .toLowerCase()
        .indexOf(userActions.get("searchText").toLowerCase()) > -1;
    const filterCardRows = (id, merchant, number, type, refID, value) =>
      `#${id} ${merchant} xxxx${number} ${type} ${refID} $${value} N/A`
        .toLowerCase()
        .indexOf(userActions.get("searchText").toLowerCase()) > -1;

    if (currentPage.includes("orders")) {
      return filterOrderRows(v.orderId, v.status);
    } else if (currentPage.includes("payments")) {
      return filterRowsById(v.id);
    } else if (currentPage.includes("cards")) {
      return filterCardRows(
        v.internalId,
        v.merchantName,
        v.number,
        v.type,
        v.refID,
        v.enteredValue
      );
    } else if (currentPage.includes("deductions")) {
      return filterRowsById(v.id);
    } else {
      return filterOrderRows(v.orderId, v.status);
    }
  };

  sortRows = (a, b) => {
    const { userActions } = this.props;
    const sortOrder = userActions.get("sortOrder");

    switch (userActions.get("fieldToSort")) {
      case "order id":
        return sortOrder === "asc"
          ? sortNumsAsc(a.orderId, b.orderId)
          : sortNumsDesc(a.orderId, b.orderId);
      case "order placed":
        if (a.date && b.date) {
          return sortOrder === "asc"
            ? sortNumsAsc(
                moment.utc(a.date.placed).valueOf(),
                moment.utc(b.date.placed).valueOf()
              )
            : sortNumsDesc(
                moment.utc(a.date.placed).valueOf(),
                moment.utc(b.date.placed).valueOf()
              );
        }
        break;
      case "order status":
        return sortOrder === "asc"
          ? sortAlphaAsc(a.status, b.status)
          : sortAlphaDesc(a.status, b.status);
      case "order status":
        return sortOrder === "asc"
          ? sortAlphaAsc(a.status, b.status)
          : sortAlphaDesc(a.status, b.status);
      case "offer amount":
        return sortOrder === "asc"
          ? sortNumsAsc(a.cardsValue, b.cardsValue)
          : sortNumsDesc(a.cardsValue, b.cardsValue);
      case "payment id":
        return sortOrder === "asc"
          ? sortNumsAsc(a.id, b.id)
          : sortNumsDesc(a.id, b.id);
      case "payout amount":
        return sortOrder === "asc"
          ? sortNumsAsc(a.amount, b.amount)
          : sortNumsDesc(a.amount, b.amount);
      case "payment status":
        return sortOrder === "asc"
          ? sortAlphaAsc(a.status, b.status)
          : sortAlphaDesc(a.status, b.status);
      case "card id":
        return sortOrder === "asc"
          ? sortNumsAsc(a.internalId, b.internalId)
          : sortNumsDesc(a.internalId, b.internalId);
      case "merchant":
        return sortOrder === "asc"
          ? sortAlphaAsc(a.merchantName, b.merchantName)
          : sortAlphaDesc(a.merchantName, b.merchantName);
      case "number":
        return sortOrder === "asc"
          ? sortNumsAsc(a.number, b.number)
          : sortNumsDesc(a.number, b.number);
      case "type":
        return sortOrder === "asc"
          ? sortAlphaAsc(a.type, b.type)
          : sortAlphaDesc(a.type, b.type);
      case "ref id":
        return sortOrder === "asc"
          ? sortNumsAsc(a.refID, b.refID)
          : sortNumsDesc(a.refID, b.refID);
      case "entered value":
        return sortOrder === "asc"
          ? sortNumsAsc(a.enteredValue, b.enteredValue)
          : sortNumsDesc(a.enteredValue, b.enteredValue);
      case "deduction id":
        return sortOrder === "asc"
          ? sortNumsAsc(a.id, b.id)
          : sortNumsDesc(a.id, b.id);
      case "amount":
        return sortOrder === "asc"
          ? sortNumsAsc(a.amount, b.amount)
          : sortNumsDesc(a.amount, b.amount);
      case "date":
        return sortOrder === "asc"
          ? sortNumsAsc(
              moment.utc(a.date).valueOf(),
              moment.utc(b.date).valueOf()
            )
          : sortNumsDesc(
              moment.utc(a.date).valueOf(),
              moment.utc(b.date).valueOf()
            );

      default:
    }
  };
  isSortableField = (fieldToSort) => {
    const { userActions } = this.props;
    const caretClasses =
      userActions.get("sortOrder") === "asc"
        ? "fa fa-lg fa-caret-up mar-l-10"
        : "fa fa-lg fa-caret-down mar-l-10";
    return (
      fieldToSort === userActions.get("fieldToSort") && (
        <i className={caretClasses}></i>
      )
    );
  };
  setSortOrderAndSortField = (fieldToSort, sortOrder) => {
    const { setSortOrder, setFieldToSort, resetActivePanel } = this.props;
    setFieldToSort(fieldToSort);
    sortOrder === "asc" ? setSortOrder("desc") : setSortOrder("asc");
    resetActivePanel();
  };

  updateSearchText = (e) => {
    const { resetActivePanel, setSearchText } = this.props;
    resetActivePanel();
    setSearchText(e.target.value.trim());
  };

  render() {
    const { paymentsSell, popups, userActions, userInfo } = this.props;
    const payments = paymentsSell.get("payments");
    const currentSellPayment = paymentsSell.get("payment");

    const paymentRows = payments
      .filter(this.filterBySearch)
      .sort(this.sortRows);

    let csv;

    if (payments.length) {
      csv = json2csv({
        data: payments,
        fields: [
          {
            label: "payment_id",
            value: ({ id }) => id.toString(),
          },
          {
            label: "amount",
            value: ({ amount }) => currencyFormatter(amount),
          },
          { value: "status" },
        ],
      });
    }

    if (userInfo.get("displayLoading")) {
      return <Loading showLoading />;
    }

    return (
      <div style={{ flex: "80%", padding: "2rem" }}>
        <HeadLine
          headingText={"Payments"}
          tagLine={"A list of payouts to your orders"}
        />

        <div
          className="search-container"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            position: "relative",
            top: "-70px",
          }}
        >
          <div style={{ marginRight: "2rem" }}>
            <input
              className="activity-search-box pad-r-40"
              style={{
                background: "rgba(196, 196, 196, 0.14)",
                borderRadius: "80px",
                height: "40px",
                padding: "1rem",
              }}
              placeholder="Search Payments"
              onChange={this.updateSearchText}
            />
            <i className="fa fa-lg fa-search activity-search-icon"></i>
          </div>
          <DownloadLink
            href={`data:text/csv;charset=utf-8,${encodeURI(csv)}`}
            target="_blank"
            download="payments.csv"
            rel="noreferrer"
          >
            Download CSV
          </DownloadLink>
        </div>
        {Object.keys(payments).length ? (
          <div style={{ marginTop: "-70px" }}>
            <table className="fixed-table panel-table-heading hide-sm">
              <thead>
                <tr>
                  <th
                    className="panel-column pointer"
                    onClick={this.setSortOrderAndSortField.bind(
                      null,
                      "payment id",
                      userActions.get("sortOrder")
                    )}
                  >
                    <div className="responsive-panel">
                      <span>
                        <strong>Payment ID</strong>
                      </span>
                      {this.isSortableField(
                        "payment id",
                        userActions.get("sortOrder")
                      )}
                    </div>
                  </th>
                  <th
                    className="panel-column pointer"
                    onClick={this.setSortOrderAndSortField.bind(
                      null,
                      "payout amount",
                      userActions.get("sortOrder")
                    )}
                  >
                    <div className="responsive-panel">
                      <span>
                        <strong>Payout Amount</strong>
                      </span>
                      {this.isSortableField(
                        "payout amount",
                        userActions.get("sortOrder")
                      )}
                    </div>
                  </th>
                  <th
                    className="panel-column pointer"
                    onClick={this.setSortOrderAndSortField.bind(
                      null,
                      "payment status",
                      userActions.get("sortOrder")
                    )}
                  >
                    <div className="responsive-panel">
                      <span>
                        <strong>Status</strong>
                      </span>
                      {this.isSortableField(
                        "payment status",
                        userActions.get("sortOrder")
                      )}
                    </div>
                  </th>
                  <th className="panel-column-sm" />
                </tr>
              </thead>
            </table>
            <div className="order-panel-premier">
              {paymentRows.length ? (
                <WindowScroller>
                  {({ height }) => (
                    <AutoSizer disableHeight>
                      {({ width }) => {
                        this._resizeAll();
                        return (
                          <List
                            height={height - 225}
                            overscanRowCount={4}
                            rowCount={paymentRows.length}
                            rowHeight={this._cache.rowHeight}
                            rowRenderer={({ key, index, parent, style }) => {
                              const payment = paymentRows[index];
                              const activePanelClass =
                                userActions.get("activePanel") === payment.id
                                  ? `dynamic-panel-active`
                                  : `dynamic-panel`;
                              return (
                                <CellMeasurer
                                  cache={this._cache}
                                  columnIndex={0}
                                  key={key}
                                  parent={parent}
                                  rowIndex={index}
                                >
                                  {({ measure }) => (
                                    <div
                                      className={activePanelClass}
                                      style={style}
                                      key={key}
                                    >
                                      <PaymentPanel
                                        id={payment.id}
                                        amount={payment.amount}
                                        status={payment.status}
                                        payment={currentSellPayment}
                                        activePanel={userActions.get(
                                          "activePanel"
                                        )}
                                        chooseActivePanel={
                                          this.chooseActivePanel
                                        }
                                        showLocalSpinner={popups.get(
                                          "showLocalSpinner"
                                        )}
                                        key={key}
                                      />
                                    </div>
                                  )}
                                </CellMeasurer>
                              );
                            }}
                            width={width}
                          />
                        );
                      }}
                    </AutoSizer>
                  )}
                </WindowScroller>
              ) : (
                <table className="responsive-panel-table fixed-table no-results">
                  <tbody>
                    <tr>
                      <td className="panel-column" colSpan="6">
                        No Payments Found
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ) : (
          <NoDetailsToShow text="payment" />
        )}
      </div>
    );
  }
}

export default connect(
  (state) => ({
    userActions: state.userActions,
    paymentsSell: state.paymentsSell,
    userInfo: state.userInfo,
    popups: state.popups,
  }),
  (dispatch) => ({
    recurrentSellPayment: () =>
      dispatch(defaultAction(ActionTypes.RESET_ACTIVE_PANEL)),
    setFieldToSort: (v) =>
      dispatch(defaultAction(ActionTypes.SET_FIELD_TO_SORT, v)),
    setSortOrder: (v) => dispatch(defaultAction(ActionTypes.SET_SORT_ORDER, v)),
    setActivePanel: (id) =>
      dispatch(defaultAction(ActionTypes.SET_ACTIVE_PANEL, id)),
    getSellPayments: () => dispatch(getSellPayments()),
    getSellPayment: (id) => dispatch(getSellPayment(id)),
    setPaymentTotal: (paymentTotal) =>
      dispatch(defaultAction(ActionTypes.SET_PAYMENT_TOTAL, { paymentTotal })),
    setSearchText: (v) =>
      dispatch(defaultAction(ActionTypes.SET_SEARCH_TEXT, v)),
    clearSearchText: () =>
      dispatch(defaultAction(ActionTypes.RESET_SEARCH_TEXT)),
    resetActivePanel: () =>
      dispatch(defaultAction(ActionTypes.RESET_ACTIVE_PANEL)),
  })
)(Payments);
